<template>
  <div class="image-search-upload">
    <v-card-title class="d-flex align-center text-white">
        图片搜索
    </v-card-title>
    <!-- 上传区域 -->
    <div 
      class="upload-area"
      @click="$refs.fileInput.click()"
      @dragover.prevent
      @drop.prevent="handleDrop"
    >
      <input
        ref="fileInput"
        type="file"
        accept=".jpg,.jpeg,.png,.webp"
        class="hidden-input"
        @change="handleFileChange"
      >
      
      <!-- <v-icon
        icon="mdi-camera-search"
        color="primary"
        size="48"
      /> -->
      <img width="68" src="@/assets/uploadLogo.png" alt="上传" class="img-icon-one">
      <div class="text-h6 mt-4">
        {{ selectedFile ? '点击或拖拽更换图片' : '点击或拖拽上传图片搜索产品' }}
      </div>
      
      <div class="text-body-2 text-medium-emphasis mt-2">
        支持 JPG、PNG、WebP 格式
        <br>
        建议使用 Chrome、Edge、Safari 浏览器
      </div>
    </div>

    <!-- 图片预览区域 -->
    <!-- <div v-if="selectedFile" class="preview-container mt-4">
      <div class="preview-header">
        <v-icon icon="mdi-image" color="primary" class="mr-2" />
        <span class="text-h6">图片预览</span>
        <v-spacer />
        <v-btn
          icon="mdi-close"
          variant="text"
          size="small"
          @click="clearFile"
        />
      </div>
      
      <div class="preview-content">
        <img
          v-if="previewUrl"
          :src="previewUrl"
          alt="预览图片"
          class="preview-image"
        />
        <div v-else class="preview-placeholder">
          <v-icon icon="mdi-file-image" size="48" color="primary" />
          <div class="text-body-2 mt-2">{{ selectedFile.name }}</div>
        </div>
      </div>
    </div> -->

    <!-- 搜索按钮 -->
    <!-- <div v-if="selectedFile" class="search-actions mt-4">
      <v-btn
        color="primary"
        variant="flat"
        size="large"
        :loading="isSearching"
        :disabled="!selectedFile || isSearching"
        @click="handleSearch"
        class="search-btn"
      >
        <v-icon icon="mdi-magnify" class="mr-2" />
        {{ isSearching ? '搜索中...' : '开始搜索' }}
      </v-btn>
    </div> -->

    <!-- 搜索结果 -->
    <div v-if="searchResults.length > 0" class="search-results mt-4">
      <div class="results-header">
        <v-icon icon="mdi-format-list-bulleted" color="primary" class="mr-2" />
        <span class="text-h6">搜索结果 ({{ searchResults.length }} 个产品)</span>
      </div>
      
      <!-- <v-list class="results-list" color="#2B2D33">
        <v-list-item
          v-for="(item, index) in searchResults"
          :key="index"
          class="result-item"
          @click="handleSelectProduct(item)"
        >
          <template v-slot:prepend>
            <v-avatar color="primary" size="40">
              <v-icon icon="mdi-package-variant" color="white" />
            </v-avatar>
          </template>

          <v-list-item-title class="product-name">
            {{ item.name || '未命名产品' }}
          </v-list-item-title>

          <v-list-item-subtitle class="product-info">
            <div>产品编号: {{ item.no || '-' }}</div>
            <div>批次: {{ item.batch || '-' }}</div>
            <div>数量: {{ item.count || '-' }}</div>
          </v-list-item-subtitle>

          <template v-slot:append>
            <v-btn
              icon="mdi-arrow-right"
              variant="text"
              size="small"
            />
          </template>
        </v-list-item>
      </v-list> -->
    </div>

    <!-- 无结果提示 -->
    <div v-else-if="hasSearched && !isSearching" class="no-results mt-4">
      <v-icon icon="mdi-magnify-close" size="48" color="warning" class="mb-4" />
      <div class="text-h6">未找到相似产品</div>
      <div class="text-body-2">请尝试上传其他图片或调整搜索参数</div>
    </div>

    <!-- 错误提示 -->
    <v-alert
      v-if="error"
      type="error"
      variant="tonal"
      class="mt-4"
      closable
      @click:close="error = null"
    >
      {{ error }}
    </v-alert>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { contractApi, uploadApi } from '@/services/api'

// Props
const props = defineProps<{
  modelValue?: boolean
}>()

// Emits
const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'search', searchImg: string): void
  (e: 'selectProduct', product: any): void
}>()

// 状态
const selectedFile = ref<File | null>(null)
const previewUrl = ref<string>('')
const isSearching = ref(false)
const searchResults = ref<any[]>([])
const hasSearched = ref(false)
const error = ref<string | null>(null)

// 计算属性
const dialogVisible = computed({
  get() {
    return props.modelValue || false
  },
  set(value) {
    emit('update:modelValue', value)
  }
})

// 监听文件变化，生成预览
watch(selectedFile, (newFile) => {
  if (newFile && newFile.type.startsWith('image/')) {
    const reader = new FileReader()
    reader.onload = (e) => {
      previewUrl.value = e.target?.result as string
      handleSearch()
    }
    reader.readAsDataURL(newFile)
  } else {
    previewUrl.value = ''
  }
})

// 验证文件类型
const isValidFileType = (file: File) => {
  const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf']
  return validTypes.includes(file.type)
}

// 处理文件选择
const handleFileChange = (event: Event) => {
  const input = event.target as HTMLInputElement
  if (!input.files?.length) return
  
  const file = input.files[0]
  if (!isValidFileType(file)) {
    error.value = '不支持的文件格式，请选择 JPG、PNG、WebP 或 PDF 文件'
    return
  }
  
  selectedFile.value = file
  error.value = null
  searchResults.value = []
  hasSearched.value = false
}

// 处理拖放
const handleDrop = (event: DragEvent) => {
  if (!event.dataTransfer?.files.length) return
  
  const file = event.dataTransfer.files[0]
  if (!isValidFileType(file)) {
    error.value = '不支持的文件格式，请选择 JPG、PNG、WebP 或 PDF 文件'
    return
  }
  
  selectedFile.value = file
  error.value = null
  searchResults.value = []
  hasSearched.value = false
}

// 清除文件
const clearFile = () => {
  selectedFile.value = null
  previewUrl.value = ''
  searchResults.value = []
  hasSearched.value = false
  error.value = null
}

// 处理搜索
const handleSearch = async () => {
  if (!selectedFile.value) {
    error.value = '请先选择图片文件'
    return
  }

  isSearching.value = true
  error.value = null
  searchResults.value = []

  try {
    // 先上传图片
    const uploadedUrls = await uploadApi.uploadFiles([selectedFile.value])
    
    if (uploadedUrls.length === 0) {
      throw new Error('图片上传失败')
    }

    emit('search',uploadedUrls[0])
  } catch (err: any) {
    console.error('图片搜索失败:', err)
    error.value = err.message || '搜索失败，请重试'
  } finally {
    isSearching.value = false
  }
}

// 处理选择产品
const handleSelectProduct = (product: any) => {
  emit('selectProduct', product)
}
</script>

<style scoped>
.image-search-upload {
  padding: 16px;
  background-color: #2B2D33;
  border-radius: 8px;
}

.upload-area {
  border: 2px dashed rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  padding: 32px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
}

.upload-area:hover {
  border-color: var(--v-primary-base);
  background: rgba(var(--v-primary-base), 0.05);
}

.hidden-input {
  display: none;
}

.preview-container {
  background-color: #3A3B3F;
  border-radius: 8px;
  overflow: hidden;
}

.preview-header {
  display: flex;
  align-items: center;
  padding: 16px;
  background-color: rgba(255, 255, 255, 0.02);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.preview-content {
  padding: 16px;
  text-align: center;
}

.preview-image {
  max-width: 100%;
  max-height: 300px;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.preview-placeholder {
  padding: 32px;
  color: rgba(255, 255, 255, 0.7);
}

.search-actions {
  text-align: center;
}

.search-btn {
  min-width: 200px;
  background: linear-gradient(45deg, #E34C65, #D2475D) !important;
  color: white !important;
  box-shadow: 0 4px 12px rgba(227, 76, 101, 0.3) !important;
}

.search-btn:hover {
  box-shadow: 0 6px 16px rgba(227, 76, 101, 0.4) !important;
}

.search-results {
  background-color: #3A3B3F;
  border-radius: 8px;
  overflow: hidden;
}

.results-header {
  display: flex;
  align-items: center;
  padding: 16px;
  background-color: rgba(255, 255, 255, 0.02);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.results-list {
  max-height: 400px;
  overflow-y: auto;
}

.result-item {
  cursor: pointer;
  transition: background-color 0.2s;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.result-item:hover {
  background-color: rgba(255, 255, 255, 0.05);
}

.result-item:last-child {
  border-bottom: none;
}

.product-name {
  color: rgba(255, 255, 255, 0.9) !important;
  font-weight: 500;
}

.product-info {
  color: rgba(255, 255, 255, 0.6) !important;
  font-size: 0.875rem;
  margin-top: 4px;
}

.no-results {
  text-align: center;
  padding: 48px 16px;
  color: rgba(255, 255, 255, 0.7);
}

.text-h6 {
  color: rgba(255, 255, 255, 0.9) !important;
}

.text-body-2 {
  color: rgba(255, 255, 255, 0.6) !important;
}

.text-medium-emphasis {
  color: rgba(255, 255, 255, 0.6) !important;
}
</style>
